import React, {useEffect} from "react";
import PayPalExpressButtons from "./PayPalExpressButtons";
import {DISPATCH_ACTION, usePayPalScriptReducer} from "@paypal/react-paypal-js";
import {useCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import shoppingCartStyles from "../../cart/ShoppingCart/ShoppingCart.module.scss";
import PayPalSingleButton from "./PayPalSingleButton";
import ErrorBoundary from "../../utils/ErrorBoundary";
import {ButtonPropType, ClientTokenType} from "../../../helpers/paypal/paypalTypes";

/**
 * This component mainly checks whether there is a braintree token or not, and if so,
 * it dispatches a `resetOptions` with the braintree token and th derived client id
 * @param props properties of type `ButtonPropType`
 * @constructor
 */
const BraintreeButtonLoader: React.FC<ButtonPropType> = (props) => {
  const [{options, isResolved}, dispatch] = usePayPalScriptReducer();
  const {braintreeToken} = useCartContext();

  useEffect(() => {
    if (braintreeToken) {
      if (!options.dataClientToken) {
        const clientId = (JSON.parse(atob(braintreeToken)) as ClientTokenType).paypal.clientId;
        dispatch({
          type: DISPATCH_ACTION.RESET_OPTIONS,
          value: {
            ...options,
            dataClientToken: braintreeToken,
            commit: !props.isExpress,
            clientId,
          },
        });
      } else if (props.isExpress === options.commit) {
        dispatch({
          type: DISPATCH_ACTION.RESET_OPTIONS,
          value: {
            ...options,
            commit: !props.isExpress,
          },
        });
      }
    }
  }, [braintreeToken, options.dataClientToken]);

  // Did we retrieve the braintree token? if so, has the dispatch finished the `resetOptions`
  if (!braintreeToken || !options.dataClientToken || props.isExpress === options.commit || !isResolved) {
    return <PayPalLoadingSpinner />;
  }

  return (
    <ErrorBoundary
      handleError={props.handleError}
      fallback={null}
      occurrence={`PayPal${props.isExpress ? "Express" : ""} Error boundary`}
    >
      {props.isExpress ? (
        <>
          <div className={shoppingCartStyles.separator}>
            <span>Express Checkouts</span>
          </div>
          <PayPalExpressButtons {...props} />
        </>
      ) : (
        <PayPalSingleButton {...props} />
      )}
    </ErrorBoundary>
  );
};

export default BraintreeButtonLoader;

/**
 * Loading spinner just for the PayPal checkout buttons
 * @constructor
 */
export const PayPalLoadingSpinner = () => {
  return (
    <div className="paypal-loading-div">
      <div className="swiper-lazy-preloader">{/* empty div for loading spinner */}</div>
      &nbsp;
    </div>
  );
};

/**
 * [Styling options](https://developer.paypal.com/docs/business/checkout/reference/style-guide/#customize-the-payment-buttons) for customizing the button appearance.
 */
type PayPalLabelType = "checkout" | "paypal";
type PayPalLayoutType = "vertical" | "horizontal";
export const buttonStyleObjectExpress = {
  height: 40,
  label: "checkout" as PayPalLabelType,
  layout: "vertical" as PayPalLayoutType,
};

export const buttonStyleObjectSummary = {
  ...buttonStyleObjectExpress,
  label: "paypal" as PayPalLabelType,
  layout: "horizontal" as PayPalLayoutType,
  tagline: false,
};
